import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FiLock, FiUnlock, FiDownload, FiRotateCcw, FiUsers, FiTrendingUp, FiGrid, FiEdit2,
} from "react-icons/fi";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend } from "recharts";
import {
  getCalibrationBoard, setCalibratedRating, resetCalibratedRating,
  getCalibrationDistribution, lockCalibration, unlockCalibration, getCalibrationLog,
} from "../../services/calibrationApi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import useAsync from "../../hooks/useAsync";
import {
  Card, PageHeader, Button, DataTable, Banner, Modal, FormField, Tabs, StatTile,
  Loader, ErrorState, ChartFrame, ConfirmDialog, SearchInput,
  BackendPending, OrgFilters,
} from "../../components/common";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import { singleValue } from "../../utils/orgFilters";
import { PersonCell } from "../../components/common/Avatar";
import RatingPill from "../../components/performance/RatingPill";
import {
  CALIBRATION_STATUS, RATING_BANDS, POTENTIAL_LEVELS,
} from "../../utils/constants";
import { DEFAULT_BANDS } from "../../utils/scoreCalculator";
import { downloadXlsx } from "../../utils/exportExcel";
import { formatDateTime, formatScore } from "../../utils/format";
import { axisProps, tooltipProps } from "../../utils/chartTheme";
import { getErrorMessage } from "../../utils/errors";
import { VIEWS, FEATURES } from "../../services/navigation";
import Select from "../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)
import { formatRatingScore } from "../../utils/ratingScale"; // [NEW] PM-13: score on the chosen scale

const api = {
  getCalibrationBoard, setCalibratedRating, resetCalibratedRating,
  getCalibrationDistribution, lockCalibration, unlockCalibration, getCalibrationLog,
};

const bandTone = (rating) => DEFAULT_BANDS.find((b) => b.label === rating)?.tone || "neutral";
const finalRating = (row) => row.calibratedRating || row.proposedRating;

function DistributionChart({ rows }) {
  const data = rows.map((r) => ({ name: r.rating.replace(" Expectations", ""), Actual: r.percent, Target: r.targetPercent, variance: r.variance }));
  return (
    <ChartFrame
      label="Rating distribution against the guide curve"
      summary={rows.map((r) => ({ label: r.rating, value: `${r.count} people, ${r.percent}% against a ${r.targetPercent}% guide` }))}
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF0F6" />
          <XAxis dataKey="name" {...axisProps} />
          <YAxis unit="%" {...axisProps} />
          <Tooltip {...tooltipProps} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Target" fill="#D7DAE6" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Actual" radius={[4, 4, 0, 0]}>
            {data.map((entry) => (
              <Cell key={entry.name} fill={Math.abs(entry.variance) > 10 ? "#D64545" : Math.abs(entry.variance) > 5 ? "#D98A0B" : "#5B4FE0"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

const PERFORMANCE_AXIS = [
  { key: "high", label: "High", ratings: ["Exceptional", "Exceeds Expectations"] },
  { key: "mid", label: "Medium", ratings: ["Meets Expectations"] },
  { key: "low", label: "Low", ratings: ["Needs Improvement", "Unsatisfactory"] },
];

const BOX_LABELS = {
  "high-High": "Star", "high-Medium": "High performer", "high-Low": "Trusted expert",
  "mid-High": "Growth potential", "mid-Medium": "Core player", "mid-Low": "Steady",
  "low-High": "Rough diamond", "low-Medium": "Inconsistent", "low-Low": "Needs action",
};

function NineBox({ employees, onPick }) {
  return (
    <div className="ninebox">
      <span className="ninebox-axis-y">Performance →</span>
      {PERFORMANCE_AXIS.map((band) =>
        POTENTIAL_LEVELS.map((potential) => {
          const cell = employees.filter((e) => band.ratings.includes(finalRating(e)) && e.potential === potential);
          const key = `${band.key}-${potential}`;
          return (
            <div key={key} className={`ninebox-cell tone-${band.key}`}>
              <p className="ninebox-label">{BOX_LABELS[key]}<span>{cell.length}</span></p>
              <ul>
                {cell.map((e) => (
                  <li key={e.employeeId}>
                    <button type="button" className="link-btn" onClick={() => onPick(e)}>{e.employeeName}</button>
                  </li>
                ))}
                {!cell.length && <li className="muted">—</li>}
              </ul>
            </div>
          );
        })
      )}
      <span className="ninebox-axis-x">Potential →</span>
    </div>
  );
}

function CalibrationPageScreen() {
  const { cycleId, cycle } = useCycle();
  const { activeView } = useRole();
  const org = useOrgFilters(); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity
  // The guide-curve distribution comes from the server and only knows the department filter.
  const departmentId = singleValue(org.filters.departmentId); // PM-18: one department for the server, else all
  const [tab, setTab] = useState("board");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [lockOpen, setLockOpen] = useState(false);
  const [lockNote, setLockNote] = useState("");
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const readOnly = activeView === VIEWS.MANAGEMENT;

  const board = useAsync(() => api.getCalibrationBoard(cycleId, {}), [cycleId], { skip: !cycleId });
  const distribution = useAsync(() => api.getCalibrationDistribution(cycleId, { departmentId }), [cycleId, departmentId], { skip: !cycleId });
  const log = useAsync(() => api.getCalibrationLog(cycleId), [cycleId], { skip: !cycleId });

  const data = board.data;
  const employees = useMemo(() => (data?.employees || []).filter((e) => org.matches({ id: e.employeeId, departmentId: e.departmentId })), [data, org.matches]); // eslint-disable-line react-hooks/exhaustive-deps
  const locked = data?.status === CALIBRATION_STATUS.LOCKED;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => !q || e.employeeName.toLowerCase().includes(q) || e.departmentName.toLowerCase().includes(q));
  }, [employees, search]);

  const moved = employees.filter((e) => e.calibratedRating && e.calibratedRating !== e.proposedRating);
  const confirmed = employees.filter((e) => e.calibratedRating);
  const outOfCurve = (distribution.data || []).filter((d) => Math.abs(d.variance) > 5);
  const average = employees.length ? employees.reduce((t, e) => t + e.finalScore, 0) / employees.length : null;

  const act = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      board.reload();
      distribution.reload();
      log.reload();
      setEditing(null);
      setLockOpen(false);
      setUnlockOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveRating = () => {
    if (editing.calibratedRating !== editing.proposedRating && editing.changeReason.trim().length < 10) {
      toast.error("Write why the rating is being changed. It goes into the audit trail.");
      return;
    }
    act(
      () => api.setCalibratedRating(cycleId, editing.employeeId, {
        calibratedRating: editing.calibratedRating,
        potential: editing.potential,
        changeReason: editing.changeReason,
      }),
      `${editing.employeeName} calibrated.`
    );
  };

  const exportBoard = () =>
    downloadXlsx(`calibration-${cycle?.name || ""}`, [
      {
        name: "Calibration board",
        title: `Calibration — ${cycle?.name || ""}${org.active ? " (filtered)" : ""}`,
        columns: [
          { key: "employeeName", label: "Employee" },
          { key: "designationName", label: "Designation" },
          { key: "departmentName", label: "Department" },
          { key: "managerName", label: "Manager" },
          { key: "finalScore", label: "Final score" },
          { key: "proposedRating", label: "System rating" },
          { key: "calibratedRating", label: "Calibrated rating", value: (r) => r.calibratedRating || "Not calibrated" },
          { key: "potential", label: "Potential" },
          { key: "changeReason", label: "Reason for change" },
          { key: "changedBy", label: "Changed by" },
        ],
        rows: employees,
      },
      {
        name: "Distribution",
        columns: [
          { key: "rating", label: "Rating" }, { key: "count", label: "People" },
          { key: "percent", label: "Actual %" }, { key: "targetPercent", label: "Guide %" }, { key: "variance", label: "Variance" },
        ],
        rows: distribution.data || [],
      },
      {
        name: "Change log",
        columns: [
          { key: "at", label: "When", value: (r) => formatDateTime(r.at) }, { key: "by", label: "By" },
          { key: "employeeName", label: "Employee" }, { key: "from", label: "From" }, { key: "to", label: "To" }, { key: "reason", label: "Reason" },
        ],
        rows: log.data || [],
      },
    ]);

  if (board.error) return <ErrorState message={board.error} onRetry={board.reload} />;
  if (!data) return <Loader />;

  return (
    <div className="page">
      <PageHeader
        title="Calibration"
        subtitle={`Moderate ratings across ${cycle?.name} before results are finalised, so the same bar applies everywhere.`}
        actions={
          <>
            <Button variant="secondary" icon={FiDownload} onClick={exportBoard} disabled={!employees.length}>Export Excel</Button>
            {!readOnly && (locked
              ? <Button variant="secondary" icon={FiUnlock} onClick={() => setUnlockOpen(true)}>Unlock</Button>
              : <Button icon={FiLock} onClick={() => setLockOpen(true)}>Lock calibration</Button>)}
          </>
        }
      />

      {locked && (
        <Banner tone="neutral" title="Calibration locked">
          Locked by {data.lockedBy} on {formatDateTime(data.lockedAt)}. Ratings are frozen and have been pushed to the
          results screen. Unlock to make further changes.
        </Banner>
      )}
      {!locked && outOfCurve.length > 0 && (
        <Banner tone="warning" title={`${outOfCurve.length} ${outOfCurve.length === 1 ? "band is" : "bands are"} off the guide curve`}>
          {outOfCurve.map((d) => `${d.rating} is ${d.variance > 0 ? "over" : "under"} by ${Math.abs(d.variance)}%`).join(", ")}. The
          curve is guidance, not a quota, but large gaps are worth discussing in the panel.
        </Banner>
      )}

      <div className="stat-grid">
        <StatTile label="In scope" value={employees.length} icon={FiUsers} hint={org.active ? "Matching the filters" : "Across the organisation"} />
        <StatTile label="Calibrated" value={`${confirmed.length}/${employees.length}`} icon={FiGrid} tone={confirmed.length === employees.length ? "success" : "warning"} hint="Confirmed by a panel" />
        <StatTile label="Ratings moved" value={moved.length} icon={FiEdit2} tone="info" hint="Changed from the system rating" />
        <StatTile label="Average score" value={average ? formatScore(average, 1) : "—"} icon={FiTrendingUp} hint="Before calibration" />
      </div>

      <div className="filter-bar">
        <OrgFilters org={org} />
        <SearchInput value={search} onChange={setSearch} placeholder="Find a person" />
        <span className="muted">{rows.length} people</span>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "board", label: "Board", count: rows.length },
          { key: "distribution", label: "Distribution" },
          { key: "ninebox", label: "9-box grid" },
          { key: "sessions", label: "Panels", count: (data.sessions || []).length },
          { key: "log", label: "Change log", count: (log.data || []).length },
        ]}
      />

      {tab === "board" && (
        <Card padded={false} title="Ratings" subtitle="Click a row to move someone. Every change needs a reason.">
          <DataTable
            rows={rows}
            rowKey="employeeId"
            pageSize={12}
            onRowClick={readOnly || locked ? undefined : (row) => setEditing({ ...row, calibratedRating: finalRating(row), changeReason: row.changeReason || "" })}
            columns={[
              { key: "employeeName", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={`${r.designationName}, ${r.departmentName}`} /> },
              { key: "managerName", label: "Manager" },
              { key: "finalScore", label: "Score", align: "right", render: (r) => <strong>{formatRatingScore(r.finalScore)}</strong> },
              { key: "proposedRating", label: "System says", render: (r) => <RatingPill rating={r.proposedRating} tone={bandTone(r.proposedRating)} size="sm" /> },
              {
                key: "calibratedRating",
                label: "Calibrated",
                render: (r) =>
                  r.calibratedRating ? (
                    <span className="calibrated-cell">
                      <RatingPill rating={r.calibratedRating} tone={bandTone(r.calibratedRating)} size="sm" />
                      {r.calibratedRating !== r.proposedRating && <span className="badge badge-warning"><span className="badge-dot" />Moved</span>}
                    </span>
                  ) : (
                    <span className="muted">Not calibrated</span>
                  ),
              },
              { key: "potential", label: "Potential", render: (r) => <span className="tag tag-info">{r.potential}</span> },
              {
                key: "act",
                label: "",
                align: "right",
                render: (r) =>
                  readOnly || locked ? null : (
                    <span className="row-actions">
                      {r.calibratedRating && (
                        <Button
                          variant="ghost" size="sm" icon={FiRotateCcw} aria-label={`Reset ${r.employeeName}`}
                          onClick={(e) => { e.stopPropagation(); act(() => api.resetCalibratedRating(cycleId, r.employeeId), "Reset to the system rating."); }}
                        />
                      )}
                      <Button variant="secondary" size="sm">Calibrate</Button>
                    </span>
                  ),
              },
            ]}
          />
        </Card>
      )}

      {tab === "distribution" && (
        <div className="grid-2-1">
          <Card title="Rating distribution" subtitle={`Bars in red or amber are more than 5 points off the guide curve.${(Object.entries(org.filters).some(([k, v]) => v.length > 0 && k !== "departmentId") || org.filters.departmentId.length > 1) ? " This view follows one Department filter only." : ""}`}>
            {distribution.loading && !distribution.data ? <Loader /> : <DistributionChart rows={distribution.data || []} />}
          </Card>
          <Card title="Band by band" padded={false}>
            <DataTable keepOrder
              rows={distribution.data || []}
              rowKey="rating"
              dense
              columns={[
                { key: "rating", label: "Rating" },
                { key: "count", label: "People", align: "right" },
                { key: "percent", label: "Actual", align: "right", render: (r) => `${r.percent}%` },
                { key: "targetPercent", label: "Guide", align: "right", render: (r) => `${r.targetPercent}%` },
                {
                  key: "variance",
                  label: "Variance",
                  align: "right",
                  render: (r) => (
                    <span className={`badge badge-${Math.abs(r.variance) > 10 ? "danger" : Math.abs(r.variance) > 5 ? "warning" : "success"}`}>
                      <span className="badge-dot" />{r.variance > 0 ? "+" : ""}{r.variance}
                    </span>
                  ),
                },
              ]}
            />
          </Card>
        </div>
      )}

      {tab === "ninebox" && (
        <Card title="9-box grid" subtitle="Performance against potential. Click a name to calibrate that person.">
          <NineBox
            employees={rows}
            onPick={(person) => !readOnly && !locked && setEditing({ ...person, calibratedRating: finalRating(person), changeReason: person.changeReason || "" })}
          />
        </Card>
      )}

      {tab === "sessions" && (
        <Card padded={false} title="Calibration panels">
          <DataTable keepOrder
            rows={data.sessions || []}
            columns={[
              { key: "name", label: "Panel" },
              { key: "date", label: "Date", render: (r) => formatDateTime(r.date) },
              { key: "chair", label: "Chaired by" },
              { key: "participants", label: "Participants", align: "right" },
              { key: "covered", label: "People covered", align: "right" },
            ]}
          />
        </Card>
      )}

      {tab === "log" && (
        <Card padded={false} title="Every change, with its reason">
          <DataTable keepOrder
            rows={log.data || []}
            pageSize={10}
            columns={[
              { key: "at", label: "When", render: (r) => formatDateTime(r.at) },
              { key: "employeeName", label: "Employee" },
              { key: "from", label: "From", render: (r) => <RatingPill rating={r.from} tone={bandTone(r.from)} size="sm" /> },
              { key: "to", label: "To", render: (r) => <RatingPill rating={r.to} tone={bandTone(r.to)} size="sm" /> },
              { key: "by", label: "Changed by" },
              { key: "reason", label: "Reason", render: (r) => <span className="muted">{r.reason}</span> },
            ]}
          />
        </Card>
      )}

      <Modal
        open={Boolean(editing)}
        size="md"
        title={editing ? `Calibrate ${editing.employeeName}` : ""}
        subtitle={editing ? `Score ${formatRatingScore(editing.finalScore)}, system rating ${editing.proposedRating}` : ""}
        onClose={busy ? undefined : () => setEditing(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={saveRating} loading={busy}>Save rating</Button>
          </>
        }
      >
        {editing && (
          <div className="form-stack">
            <FormField label="Calibrated rating" required>
              <Select keepOrder value={editing.calibratedRating} onChange={(e) => setEditing({ ...editing, calibratedRating: e.target.value })}>
                {RATING_BANDS.map((band) => <option key={band} value={band}>{band}</option>)}
              </Select>
            </FormField>
            <FormField label="Potential" required hint="Used for the 9-box grid and succession planning.">
              <div className="segmented" role="radiogroup" aria-label="Potential">
                {POTENTIAL_LEVELS.map((level) => (
                  <button
                    key={level} type="button" role="radio" aria-checked={editing.potential === level}
                    className={editing.potential === level ? "is-active" : ""}
                    onClick={() => setEditing({ ...editing, potential: level })}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </FormField>
            <FormField
              label="Reason"
              required={editing.calibratedRating !== editing.proposedRating}
              hint="Recorded in the audit trail and shown to the reporting manager."
            >
              <textarea rows={3} value={editing.changeReason} onChange={(e) => setEditing({ ...editing, changeReason: e.target.value })} />
            </FormField>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={lockOpen}
        title="Lock calibration for this cycle?"
        confirmLabel="Lock calibration"
        loading={busy}
        onConfirm={() => act(() => api.lockCalibration(cycleId, { note: lockNote }), "Calibration locked.")}
        onClose={() => setLockOpen(false)}
      >
        <p className="confirm-message">
          {confirmed.length} of {employees.length} people are calibrated. Anyone not calibrated keeps the system rating.
          Locking pushes every rating to the results screen.
        </p>
        <FormField label="Panel note" hint="Optional, stored with the lock.">
          <textarea rows={3} value={lockNote} onChange={(e) => setLockNote(e.target.value)} />
        </FormField>
      </ConfirmDialog>

      <ConfirmDialog
        open={unlockOpen}
        title="Unlock calibration?"
        message="Ratings become editable again and the results screen will show them as provisional until you lock it back."
        confirmLabel="Unlock"
        tone="danger"
        loading={busy}
        onConfirm={() => act(() => api.unlockCalibration(cycleId), "Calibration unlocked.")}
        onClose={() => setUnlockOpen(false)}
      />
    </div>
  );
}

const NEEDED_ENDPOINTS = [
  ["GET", "/cycles/{cycleId}/calibration", "Board: employees, proposed and calibrated ratings"],
  ["PATCH", "/cycles/{cycleId}/calibration/{employeeId}", "Move a rating (with reason)"],
  ["POST", "/cycles/{cycleId}/calibration/reset/{employeeId}", "Undo a calibration change"],
  ["GET", "/cycles/{cycleId}/calibration/distribution", "Actual vs target distribution"],
  ["POST", "/cycles/{cycleId}/calibration/lock | unlock", "Freeze calibrated ratings into results"],
  ["GET", "/cycles/{cycleId}/calibration/log", "Change log"],
];

export default function CalibrationPage() {
  if (!FEATURES.calibration) {
    return <BackendPending title="Calibration" purpose="Moderate ratings across teams before results are released." endpoints={NEEDED_ENDPOINTS} flag="REACT_APP_FEATURE_CALIBRATION" />;
  }
  return <CalibrationPageScreen />;
}
